import type {
  AdjustWorkforceAttendanceRequest,
  AssignWorkforceShiftRequest,
  AssignWorkforceSkillRequest,
  CreateWorkforceAvailabilityRuleRequest,
  CreateWorkforceDepartmentRequest,
  CreateWorkforceEmployeeRequest,
  CreateWorkforceLeaveRequest,
  CreateWorkforceShiftRequest,
  CreateWorkforceSkillRequest,
  CreateWorkforceTimesheetRequest,
  DecideWorkforceLeaveRequest,
  RecordWorkforceAttendanceRequest,
  UpdateWorkforceSettingsRequest,
  WorkforceCommandResponse,
  WorkforceOverviewResponse
} from '@fmcgbyalex/contracts/workforce';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { ApiError, FmcgApiClient, createIdempotencyKey } from '../lib/api-client';
import { useApplication } from './ApplicationProvider';

export type WorkforceNotice = Readonly<{ tone: 'success' | 'error' | 'info'; title: string; detail: string }>;

export type WorkforceContextValue = Readonly<{
  overview: WorkforceOverviewResponse | null;
  loading: boolean;
  refreshing: boolean;
  busyAction: string | null;
  error: ApiError | null;
  notice: WorkforceNotice | null;
  refresh: () => Promise<void>;
  dismissNotice: () => void;
  updateSettings: (input: UpdateWorkforceSettingsRequest) => Promise<WorkforceCommandResponse>;
  createDepartment: (input: CreateWorkforceDepartmentRequest) => Promise<WorkforceCommandResponse>;
  createSkill: (input: CreateWorkforceSkillRequest) => Promise<WorkforceCommandResponse>;
  createEmployee: (input: CreateWorkforceEmployeeRequest) => Promise<WorkforceCommandResponse>;
  assignSkill: (employeeId: string, input: AssignWorkforceSkillRequest) => Promise<WorkforceCommandResponse>;
  createAvailability: (employeeId: string, input: CreateWorkforceAvailabilityRuleRequest) => Promise<WorkforceCommandResponse>;
  createShift: (input: CreateWorkforceShiftRequest) => Promise<WorkforceCommandResponse>;
  assignShift: (shiftId: string, input: AssignWorkforceShiftRequest) => Promise<WorkforceCommandResponse>;
  createLeave: (input: CreateWorkforceLeaveRequest) => Promise<WorkforceCommandResponse>;
  decideLeave: (leaveId: string, decision: 'approve' | 'reject', input: DecideWorkforceLeaveRequest) => Promise<WorkforceCommandResponse>;
  recordAttendance: (input: RecordWorkforceAttendanceRequest) => Promise<WorkforceCommandResponse>;
  approveAttendance: (attendanceId: string) => Promise<WorkforceCommandResponse>;
  adjustAttendance: (attendanceId: string, input: AdjustWorkforceAttendanceRequest) => Promise<WorkforceCommandResponse>;
  createTimesheet: (input: CreateWorkforceTimesheetRequest) => Promise<WorkforceCommandResponse>;
  transitionTimesheet: (timesheetId: string, transition: 'submit' | 'approve') => Promise<WorkforceCommandResponse>;
}>;

const WorkforceContext = createContext<WorkforceContextValue | null>(null);

export function WorkforceProvider({ children }: PropsWithChildren) {
  const application = useApplication();
  const [overview, setOverview] = useState<WorkforceOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<WorkforceNotice | null>(null);
  const generation = useRef(0);

  const client = useMemo(() => application.authentication ? new FmcgApiClient(application.runtime, application.authentication) : null, [application.authentication, application.runtime]);

  useEffect(() => {
    if (application.status !== 'ready' || !application.tenantId || !application.session || !client) {
      ++generation.current;
      setOverview(null);
      setLoading(false);
      setError(null);
      return;
    }
    void load(client, application.tenantId, true);
  }, [application.status, application.tenantId, application.session, client]);

  async function load(activeClient: FmcgApiClient, tenantId: string, initial: boolean): Promise<void> {
    const current = ++generation.current;
    if (initial) setLoading(true); else setRefreshing(true);
    setError(null);
    const enabled = application.session?.modules.some((module) => module.key === 'workforce' && module.enabled) ?? false;
    const readable = enabled && new Set(application.session?.permissions ?? []).has('workforce.reports.read');
    try {
      const next = readable ? await activeClient.get<WorkforceOverviewResponse>('/v1/workforce/overview', tenantId) : null;
      if (current === generation.current) setOverview(next);
    } catch (caught) {
      if (current !== generation.current) return;
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({ tone: 'error', title: apiError.problem.title, detail: apiError.problem.detail ?? 'Workforce data could not be loaded.' });
    } finally {
      if (current === generation.current) { setLoading(false); setRefreshing(false); }
    }
  }

  async function refresh(): Promise<void> { await load(requireClient(), requireTenant(), false); }

  async function mutate<T>(busy: string, title: string, detail: string, operation: (api: FmcgApiClient, tenantId: string) => Promise<T>): Promise<T> {
    const api = requireClient();
    const tenantId = requireTenant();
    setBusyAction(busy);
    setNotice(null);
    try {
      const result = await operation(api, tenantId);
      await load(api, tenantId, false);
      setNotice({ tone: 'success', title, detail });
      return result;
    } catch (caught) {
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({ tone: 'error', title: apiError.problem.title, detail: apiError.problem.detail ?? 'The workforce request failed.' });
      throw apiError;
    } finally { setBusyAction(null); }
  }

  function requireClient(): FmcgApiClient { if (!client) throw new Error('Authentication is required.'); return client; }
  function requireTenant(): string { if (!application.tenantId) throw new Error('A tenant must be selected.'); return application.tenantId; }

  const value: WorkforceContextValue = {
    overview, loading, refreshing, busyAction, error, notice, refresh, dismissNotice: () => setNotice(null),
    updateSettings: (input) => mutate('Updating workforce policy', 'Workforce policy updated', 'Standard time and overtime thresholds are active.', (api, tenantId) => api.mutate('PUT', '/v1/workforce/settings', input, tenantId, createIdempotencyKey('workforce-settings'))),
    createDepartment: (input) => mutate('Creating department', 'Department created', `${input.name} is available for employee assignment.`, (api, tenantId) => api.mutate('POST', '/v1/workforce/departments', input, tenantId, createIdempotencyKey('workforce-department'))),
    createSkill: (input) => mutate('Creating skill', 'Skill created', `${input.name} is available for certification control.`, (api, tenantId) => api.mutate('POST', '/v1/workforce/skills', input, tenantId, createIdempotencyKey('workforce-skill'))),
    createEmployee: (input) => mutate('Creating employee', 'Employee created', `${input.displayName} is available for scheduling.`, (api, tenantId) => api.mutate('POST', '/v1/workforce/employees', input, tenantId, createIdempotencyKey('workforce-employee'))),
    assignSkill: (employeeId, input) => mutate('Assigning skill', 'Employee skill updated', 'Proficiency and certification validity were recorded.', (api, tenantId) => api.mutate('POST', `/v1/workforce/employees/${encodeURIComponent(employeeId)}/skills`, input, tenantId, createIdempotencyKey('workforce-employee-skill'))),
    createAvailability: (employeeId, input) => mutate('Adding availability', 'Availability added', 'The recurring availability window is active.', (api, tenantId) => api.mutate('POST', `/v1/workforce/employees/${encodeURIComponent(employeeId)}/availability`, input, tenantId, createIdempotencyKey('workforce-availability'))),
    createShift: (input) => mutate('Creating shift', 'Shift created', `${input.shiftCode} is ready for staffing.`, (api, tenantId) => api.mutate('POST', '/v1/workforce/shifts', input, tenantId, createIdempotencyKey('workforce-shift'))),
    assignShift: (shiftId, input) => mutate('Assigning employee', 'Shift assignment created', 'Availability, leave, overlap and skill controls passed.', (api, tenantId) => api.mutate('POST', `/v1/workforce/shifts/${encodeURIComponent(shiftId)}/assignments`, input, tenantId, createIdempotencyKey('workforce-shift-assignment'))),
    createLeave: (input) => mutate('Requesting leave', 'Leave requested', 'The absence request is waiting for an independent decision.', (api, tenantId) => api.mutate('POST', '/v1/workforce/leave', input, tenantId, createIdempotencyKey('workforce-leave'))),
    decideLeave: (leaveId, decision, input) => mutate(`${decision === 'approve' ? 'Approving' : 'Rejecting'} leave`, `Leave ${decision === 'approve' ? 'approved' : 'rejected'}`, 'The absence calendar was updated.', (api, tenantId) => api.mutate('POST', `/v1/workforce/leave/${encodeURIComponent(leaveId)}/${decision}`, input, tenantId, createIdempotencyKey(`workforce-leave-${decision}`))),
    recordAttendance: (input) => mutate('Recording attendance', 'Attendance recorded', 'Worked time is ready for approval.', (api, tenantId) => api.mutate('POST', '/v1/workforce/attendance', input, tenantId, createIdempotencyKey('workforce-attendance'))),
    approveAttendance: (attendanceId) => mutate('Approving attendance', 'Attendance approved', 'Regular time, overtime and labour cost are now immutable.', (api, tenantId) => api.mutate('POST', `/v1/workforce/attendance/${encodeURIComponent(attendanceId)}/approve`, {}, tenantId, createIdempotencyKey('workforce-attendance-approve'))),
    adjustAttendance: (attendanceId, input) => mutate('Adjusting attendance', 'Attendance adjustment posted', 'The original approval remains visible with a linked correction.', (api, tenantId) => api.mutate('POST', `/v1/workforce/attendance/${encodeURIComponent(attendanceId)}/adjustments`, input, tenantId, createIdempotencyKey('workforce-attendance-adjust'))),
    createTimesheet: (input) => mutate('Creating timesheet', 'Timesheet created', 'Approved time is allocated to production and cost centres.', (api, tenantId) => api.mutate('POST', '/v1/workforce/timesheets', input, tenantId, createIdempotencyKey('workforce-timesheet'))),
    transitionTimesheet: (timesheetId, transition) => mutate(`${transition === 'approve' ? 'Approving' : 'Submitting'} timesheet`, `Timesheet ${transition === 'approve' ? 'approved' : 'submitted'}`, transition === 'approve' ? 'Labour allocation is now immutable and payroll-ready.' : 'The timesheet is ready for approval.', (api, tenantId) => api.mutate('POST', `/v1/workforce/timesheets/${encodeURIComponent(timesheetId)}/${transition}`, {}, tenantId, createIdempotencyKey(`workforce-timesheet-${transition}`)))
  };

  return <WorkforceContext.Provider value={value}>{children}</WorkforceContext.Provider>;
}

export function useWorkforce(): WorkforceContextValue {
  const context = useContext(WorkforceContext);
  if (!context) throw new Error('useWorkforce must be used within WorkforceProvider.');
  return context;
}

function toApiError(caught: unknown): ApiError {
  if (caught instanceof ApiError) return caught;
  return new ApiError(0, { type: 'https://fmcgbyalex.com/problems/client-error', title: 'The workforce request failed', status: 0, detail: caught instanceof Error ? caught.message : 'An unexpected client error occurred.' }, null);
}
